import React, { useState, useEffect, useRef } from "react";
import { ChatKit, useChatKit } from "@openai/chatkit-react";
import {
  Box,
  CircularProgress,
  Fab,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import { assistantPageCodeEnum } from "features/AssistantPages/constantTypes";
import { IChatKitSession, openChatKitSession } from "api/openChatKitSession";

interface IAssistantChatProps {
  code: assistantPageCodeEnum;
  propertyId?: string;
}

export const AssistantChat = ({ code, propertyId }: IAssistantChatProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [session, setSession] = useState<IChatKitSession | null>(null);
  const [isOpening, setIsOpening] = useState(false);
  const sessionRef = useRef<IChatKitSession | null>(null);

  useEffect(() => {
    setSession(null);
    sessionRef.current = null;
  }, [code, propertyId]);

  const { control } = useChatKit({
    api: {
      async getClientSecret(existing) {
        if (existing) {
          const refreshed = await openChatKitSession({ code, propertyId });

          if (sessionRef.current) {
            sessionRef.current = {
              ...sessionRef.current,
              clientSecret: refreshed.clientSecret,
            };
          }

          return refreshed.clientSecret;
        }

        return sessionRef.current?.clientSecret ?? "";
      },
    },
    initialThread: session?.lastThreadId ?? undefined,
    composer: {
      attachments: {
        enabled: true,
        accept: {
          "image/avif": [".avif"],
          "image/bmp": [".bmp"],
          "image/gif": [".gif"],
          "image/jpeg": [".jpeg", ".jpg"],
          "image/png": [".png"],
          "image/webp": [".webp"],
          "application/msword": [".doc"],
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
            [".docx"],
          "application/vnd.ms-powerpoint": [".ppt"],
          "application/vnd.openxmlformats-officedocument.presentationml.presentation":
            [".pptx"],
          "application/vnd.ms-excel": [".xls"],
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
            ".xlsx",
          ],
          "text/markdown": [".md"],
          "text/plain": [".txt"],
        },
      },
    },
  });

  const toggleChat = async () => {
    if (isVisible) {
      setIsVisible(false);
      return;
    }

    if (!sessionRef.current && !isOpening) {
      setIsOpening(true);
      try {
        const data = await openChatKitSession({ code, propertyId });
        sessionRef.current = data;
        setSession(data);
      } finally {
        setIsOpening(false);
      }
    }

    setIsVisible(true);
  };

  return (
    <>
      <Fab
        color="primary"
        aria-label="chat"
        onClick={toggleChat}
        disabled={isOpening}
        sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1000 }}
      >
        {isOpening ? (
          <CircularProgress size={24} color="inherit" />
        ) : isVisible ? (
          <CloseIcon />
        ) : (
          <ChatIcon />
        )}
      </Fab>

      <Paper
        elevation={6}
        sx={{
          position: "fixed",
          bottom: 90,
          right: 24,
          width: "500px",
          height: "min(800px, calc(100vh - 120px))",
          display: isVisible ? "flex" : "none",
          flexDirection: "column",
          overflow: "hidden",
          zIndex: 1000,
          borderRadius: "12px",
        }}
      >
        <Box
          sx={{
            p: 2,
            bgcolor: "primary.main",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="subtitle1" color="inherit">
            AI Assistant
          </Typography>

          <IconButton size="small" color="inherit" onClick={toggleChat}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ flexGrow: 1, position: "relative" }}>
          {session ? (
            <ChatKit key={`${code}_${propertyId}`} control={control} />
          ) : (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={28} />
            </Box>
          )}
        </Box>
      </Paper>
    </>
  );
};
